import { isMac } from './key-combo'
import { ShortcutCommand } from './shortcut.types'

/**
 * Static metadata of every built-in command, so the settings page can list
 * (and let users rebind or disable) commands of screens that aren't mounted.
 * Handlers are attached at registration time with `withRun()`.
 *
 * kind:
 *   global -> registered once at app start, active everywhere
 *   grid   -> registered by every rg-grid under the current route screen
 *   screen -> registered by one screen (list it in `screens`)
 */
export interface CommandMeta {
    id: string
    label: string
    group: string
    kind: 'global' | 'grid' | 'screen'
    defaultKeys: Array<string>
    screens?: Array<string>
    allowInModal?: boolean
}

const L = 'common.keyboard-shortcuts.commands'

export const GLOBAL_COMMANDS = {
    cheatSheet: { id: 'global.cheat-sheet', label: `${L}.cheat-sheet`, group: 'general', kind: 'global', defaultKeys: ['shift+slash'] },
    settings: { id: 'global.shortcut-settings', label: `${L}.shortcut-settings`, group: 'general', kind: 'global', defaultKeys: ['alt+shift+k'] },
    dashboard: { id: 'global.dashboard', label: `${L}.dashboard`, group: 'navigation', kind: 'global', defaultKeys: ['alt+shift+d'] },
    downloadLogs: { id: 'global.download-logs', label: `${L}.download-logs`, group: 'navigation', kind: 'global', defaultKeys: ['alt+shift+l'] },
    // Registered by the workshop panel's spotlight. The OS keeps Cmd+Space (macOS Spotlight) and
    // Alt+Space (Windows window menu), so: Option+Space on Mac, Ctrl+Space elsewhere.
    // allowInModal so the same keys close it again.
    spotlight: { id: 'global.spotlight', label: `${L}.spotlight`, group: 'navigation', kind: 'global', defaultKeys: [isMac() ? 'alt+space' : 'mod+space'], allowInModal: true }
} satisfies Record<string, CommandMeta>

export const GRID_COMMANDS = {
    focusSearch: { id: 'grid.focus-search', label: `${L}.grid-focus-search`, group: 'grid', kind: 'grid', defaultKeys: ['slash'] },
    refresh: { id: 'grid.refresh', label: `${L}.grid-refresh`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+r'] },
    clearFilters: { id: 'grid.clear-filters', label: `${L}.grid-clear-filters`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+c'] },
    toggleSelectedOnly: { id: 'grid.toggle-selected-only', label: `${L}.grid-toggle-selected-only`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+s'] },
    // Same combo starts and ends capture, like the grid's mic button
    voiceCommand: { id: 'grid.voice-command', label: `${L}.grid-voice-command`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+v'] },
    saveView: { id: 'grid.save-view', label: `${L}.grid-save-view`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+w'] },
    saveViewAsNew: { id: 'grid.save-view-as-new', label: `${L}.grid-save-view-as-new`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+n'] },
    revertView: { id: 'grid.revert-view', label: `${L}.grid-revert-view`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+z'] },
    downloadPdf: { id: 'grid.download-pdf', label: `${L}.grid-download-pdf`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+p'] },
    downloadExcel: { id: 'grid.download-excel', label: `${L}.grid-download-excel`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+x'] },
    downloadCsv: { id: 'grid.download-csv', label: `${L}.grid-download-csv`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+comma'] },
    clearSelection: { id: 'grid.clear-selection', label: `${L}.grid-clear-selection`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+u'] },
    columns: { id: 'grid.columns', label: `${L}.grid-columns`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+m'] },
    graph: { id: 'grid.graph', label: `${L}.grid-graph`, group: 'grid', kind: 'grid', defaultKeys: ['alt+shift+g'] }
} satisfies Record<string, CommandMeta>

/** Screen-specific commands; add an entry here when a screen calls injectShortcuts. */
export const SCREEN_COMMANDS: Record<string, CommandMeta> = {}

export const ALL_COMMANDS: Array<CommandMeta> = [
    ...Object.values(GLOBAL_COMMANDS),
    ...Object.values(GRID_COMMANDS),
    ...Object.values(SCREEN_COMMANDS)
]

export function findCommandMeta (id: string): CommandMeta | undefined {
    return ALL_COMMANDS.find(c => c.id === id)
}

/** Builds a registrable command from catalog metadata plus its handler. */
export function withRun (meta: CommandMeta, run: ShortcutCommand['run'], extra: Partial<ShortcutCommand> = {}): ShortcutCommand {
    return {
        id: meta.id,
        label: meta.label,
        group: meta.group,
        defaultKeys: meta.defaultKeys,
        allowInModal: meta.allowInModal,
        run,
        ...extra
    }
}
